import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { studioAPI, uploadAPI, courseAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, ChevronRight, ChevronLeft, Check, ArrowLeft, Sparkles, X, FileDown, ImagePlus, Smartphone, Monitor, Target, Zap, Users, Globe, BookOpen, Lightbulb, Mic, MousePointerClick, PenLine, RotateCcw, Copy, ChevronDown, Palette, FolderOpen, Plus, Download, Play, Trash2, PenSquare, Package } from 'lucide-react';

const INTERVIEW_QUESTIONS = [
  { key: 'topic', label: 'What is the main topic of your ebook?', placeholder: 'e.g. Social Media Marketing for Small Business', type: 'text', required: true },
  { key: 'target_audience', label: 'Who is your target audience?', placeholder: 'e.g. Business owners aged 25-40', type: 'textarea', required: true },
  { key: 'main_problem', label: 'What is the #1 problem your audience faces?', placeholder: "e.g. Can't create content that converts", type: 'textarea', required: true },
  { key: 'transformation', label: 'What result will readers get?', placeholder: 'e.g. Generate $5K/month in 90 days', type: 'textarea', required: true },
  { key: 'reader_level', label: 'Reader level?', type: 'select', options: [{ value: 'beginner', label: 'Beginner' }, { value: 'intermediate', label: 'Intermediate' }, { value: 'advanced', label: 'Advanced' }]},
  { key: 'language', label: 'Language?', type: 'select', options: [{ value: 'english', label: 'English' }, { value: 'malay', label: 'Bahasa Melayu' }, { value: 'both', label: 'Bilingual' }]},
  { key: 'ebook_length', label: 'Length?', type: 'select', options: [{ value: 'short', label: 'Short (8-12p)' }, { value: 'medium', label: 'Medium (15-22p)' }, { value: 'long', label: 'Long (25-35+p)' }]},
  { key: 'unique_method', label: 'Unique framework?', placeholder: 'e.g. "The 3R Method" or leave blank', type: 'textarea' },
  { key: 'writing_tone', label: 'Tone?', type: 'select', options: [{ value: 'casual', label: 'Casual' }, { value: 'formal', label: 'Formal' }, { value: 'motivational', label: 'Motivational' }, { value: 'storytelling', label: 'Storytelling' }]},
  { key: 'cta', label: 'CTA at the end?', placeholder: 'e.g. Link to premium course', type: 'textarea' },
];

const COLOR_MOODS = [
  { value: 'auto', label: 'Auto', colors: ['#6B7280','#9CA3AF','#D1D5DB'] },
  { value: 'warm', label: 'Warm', colors: ['#9B1B30','#C4652A','#D4A03C'] },
  { value: 'cool', label: 'Cool', colors: ['#1B4965','#5FA8D3','#E8F1F8'] },
  { value: 'dark', label: 'Dark', colors: ['#1A1A2E','#C9A95A','#F5F0E8'] },
  { value: 'vibrant', label: 'Vibrant', colors: ['#2563EB','#F43F5E','#FBBF24'] },
  { value: 'pastel', label: 'Pastel', colors: ['#7C3AED','#EC4899','#FDF4FF'] },
  { value: 'earthy', label: 'Earthy', colors: ['#92400E','#65A30D','#FEF7ED'] },
  { value: 'monochrome', label: 'Mono', colors: ['#0A0A0A','#4B5563','#FFF'] },
];

function parseChapters(text) {
  if (!text?.trim()) return [];
  const lines = text.split('\n'); const chs = []; let cur = null;
  for (const line of lines) {
    const h1 = line.match(/^#\s+(.+)/); const h2 = line.match(/^##\s+(.+)/);
    if (h1 || h2) { if (cur) chs.push(cur); cur = { title: (h1||h2)[1].trim(), content: '', level: h1?1:2 }; }
    else { if (!cur) cur = { title: 'Introduction', content: '', level: 1 }; cur.content += line + '\n'; }
  }
  if (cur) chs.push(cur);
  return chs.map(c => ({ ...c, content: c.content.trim() })).filter(c => c.content || c.title);
}
function chaptersToText(chs) { return chs.map(c => `${c.level===1?'# ':'## '}${c.title}\n\n${c.content}`).join('\n\n'); }

function extractImages(html) {
  if (!html) return [];
  const re = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  const imgs = []; let m;
  while ((m = re.exec(html)) !== null) { if (m[1] && !m[1].includes('BRAND_LOGO')) imgs.push(m[1]); }
  return [...new Set(imgs)];
}

/* No page splitting needed — scrollable preview */

export default function DigitalStudioPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [projectId, setProjectId] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [inputMode, setInputMode] = useState(null);
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [interviewAnswers, setInterviewAnswers] = useState({ topic:'',target_audience:'',main_problem:'',transformation:'',reader_level:'',language:'',ebook_length:'',unique_method:'',writing_tone:'',cta:'' });
  const [currentQ, setCurrentQ] = useState(0);
  const [savingInterview, setSavingInterview] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [draftGenerating, setDraftGenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(0);
  const [regeneratingChapter, setRegeneratingChapter] = useState(null);
  const [layout, setLayout] = useState('portrait');
  const [colorMood, setColorMood] = useState('auto');
  const [brandLogos, setBrandLogos] = useState([]);
  const [ebookImages, setEbookImages] = useState([]);
  const [logoChoice, setLogoChoice] = useState('none');
  const [imageChoice, setImageChoice] = useState('none');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [design, setDesign] = useState(null);
  const [designColors, setDesignColors] = useState({ primary:'', bg:'', text:'' });
  const [originalColors, setOriginalColors] = useState({ primary:'', bg:'', text:'' });
  const [designImages, setDesignImages] = useState([]);
  const [imageReplacements, setImageReplacements] = useState({});
  const [titleSize, setTitleSize] = useState(28);
  const [subtitleSize, setSubtitleSize] = useState(18);
  const [bodySize, setBodySize] = useState(15);
  const [captionSize, setCaptionSize] = useState(11);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showListPopup, setShowListPopup] = useState(false);
  const [listForm, setListForm] = useState({ name: '', description: '', price: 0, is_free: false, max_downloads: 5, validity_days: 365 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      studioAPI.projects().then(r => setProjects(r.data)).catch(() => {}),
      courseAPI.list().then(r => setCourses(r.data)).catch(() => {}),
    ]).finally(() => setLoadingProjects(false));
  }, []);

  const loadProject = async (pid) => {
    try {
      const r = await studioAPI.getProject(pid);
      const p = r.data;
      setProjectId(pid); setInputMode(p.interview ? 'ai' : 'upload');
      setUploadResult({ filename: p.filename, text_length: 0 });
      if (p.interview) setInterviewAnswers(p.interview);
      if (p.draft_text || p.extracted_text) setChapters(parseChapters(p.draft_text || p.extracted_text || ''));
      setLayout(p.layout || 'portrait'); setColorMood(p.color_mood || 'auto');
      setBrandLogos(p.brand_logos || []); setEbookImages(p.ebook_images || []);
      setLogoChoice(p.brand_logos?.length ? 'upload' : 'none'); setImageChoice(p.ebook_images?.length ? 'upload' : 'none');
      const d = p.designs?.[0];
      if (d) {
        setDesign(d);
        const cols = { primary: d.primary_color||'', bg: d.bg_color||'', text: d.text_color||'' };
        setDesignColors(cols); setOriginalColors(cols);
        setDesignImages(extractImages(d.html));
        setStep(4);
      } else if (p.draft_text || p.extracted_text) { setStep(3); }
      else if (p.interview) { setStep(2); } else { setStep(1); }
      toast.success('Project loaded!');
    } catch { toast.error('Failed to load'); }
  };

  const startNew = (mode) => {
    setProjectId(null); setUploadResult(null); setInputMode(mode);
    setChapters([]); setEditingIdx(null); setExpandedIdx(0);
    setInterviewAnswers({ topic:'',target_audience:'',main_problem:'',transformation:'',reader_level:'',language:'',ebook_length:'',unique_method:'',writing_tone:'',cta:'' });
    setDesign(null); setDesignColors({primary:'',bg:'',text:''}); setOriginalColors({primary:'',bg:'',text:''});
    setDesignImages([]); setImageReplacements({}); setTitleSize(28); setSubtitleSize(18); setBodySize(15); setCaptionSize(11);
    setLayout('portrait'); setColorMood('auto'); setLogoChoice('none'); setImageChoice('none');
    setBrandLogos([]); setEbookImages([]);
    setStep(1);
  };

  // Step 1 handlers
  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await studioAPI.upload(fd); setProjectId(res.data.id); setUploadResult(res.data);
      const dr = await studioAPI.getDraft(res.data.id);
      setChapters(parseChapters(dr.data.extracted_text || '')); setStep(2); toast.success('Uploaded!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setUploading(false); }
  };

  const handleGenerateDraft = async () => {
    const missing = ['topic','target_audience','main_problem','transformation'].filter(k => !interviewAnswers[k]?.trim());
    if (missing.length) { toast.error('Answer Q1-Q4'); return; }
    setSavingInterview(true);
    try {
      let pid = projectId;
      if (!pid) { const r = await studioAPI.submitIdea({ title: interviewAnswers.topic, content: interviewAnswers.topic }); pid = r.data.id; setProjectId(pid); setUploadResult({ filename: interviewAnswers.topic }); }
      await studioAPI.saveInterview(pid, interviewAnswers);
      setStep(2); setDraftGenerating(true);
      await studioAPI.generateDraft(pid); pollDraft(pid);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); setDraftGenerating(false); }
    finally { setSavingInterview(false); }
  };

  const pollDraft = useCallback((pid) => {
    const iv = setInterval(async () => {
      try {
        const r = await studioAPI.getDraft(pid);
        if (r.data.status === 'done') { clearInterval(iv); setChapters(parseChapters(r.data.draft_text||'')); setDraftGenerating(false); toast.success('Draft ready!'); }
        else if (r.data.status === 'failed') { clearInterval(iv); setDraftGenerating(false); toast.error('Failed'); }
      } catch { /* polling */ }
    }, 4000);
    setTimeout(() => { clearInterval(iv); setDraftGenerating(false); }, 300000);
  }, []);

  const handleRegenerateChapter = async (idx) => {
    const ch = chapters[idx]; setRegeneratingChapter(idx);
    try {
      await studioAPI.regenerateChapter(projectId, { chapter_index: idx, chapter_title: ch.title, context: ch.content });
      const poll = setInterval(async () => {
        try {
          const r = await studioAPI.chapterStatus(projectId);
          if (r.data.status === 'done' && r.data.chapter_index === idx) { clearInterval(poll); const u = [...chapters]; u[idx] = { ...u[idx], content: r.data.content }; setChapters(u); setRegeneratingChapter(null); toast.success('Regenerated!'); }
          else if (r.data.status === 'failed') { clearInterval(poll); setRegeneratingChapter(null); toast.error('Failed'); }
        } catch { /* polling */ }
      }, 4000);
      setTimeout(() => { clearInterval(poll); setRegeneratingChapter(null); }, 180000);
    } catch { setRegeneratingChapter(null); toast.error('Failed'); }
  };

  const handleConfirmDraft = async () => {
    const t = chaptersToText(chapters); if (!t.trim()) return;
    setSavingDraft(true);
    try { await studioAPI.saveDraft(projectId, { draft_text: t }); setStep(3); toast.success('Draft saved!'); }
    catch { toast.error('Failed'); } finally { setSavingDraft(false); }
  };

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied!`); }
    catch { const ta=document.createElement('textarea'); ta.value=text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); toast.success(`${label} copied!`); }
  };

  // Step 3 handlers
  const handleLogoUpload = async (e) => { const f=e.target.files?.[0]; if(!f) return; setUploadingLogo(true); try { const fd=new FormData(); fd.append('file',f); const r=await uploadAPI.thumbnail(fd); setBrandLogos(p=>[...p,r.data.thumbnail_url]); setLogoChoice('upload'); } catch { toast.error('Failed'); } finally { setUploadingLogo(false); e.target.value=''; } };
  const handleImageUpload = async (e) => { const files=Array.from(e.target.files||[]); if(!files.length) return; setUploadingImage(true); try { for (const f of files) { if(ebookImages.length>=20) break; const fd=new FormData(); fd.append('file',f); const r=await uploadAPI.thumbnail(fd); setEbookImages(p=>[...p,r.data.thumbnail_url]); setImageChoice('upload'); } } catch { toast.error('Failed'); } finally { setUploadingImage(false); e.target.value=''; } };

  const handleGenerate = async () => {
    setGenerating(true); setDesign(null);
    try {
      await studioAPI.generate(projectId, { layout, color_mood: colorMood, brand_logos: brandLogos, ebook_images: ebookImages });
      const poll = setInterval(async () => {
        try {
          const r = await studioAPI.status(projectId);
          if (r.data.status === 'done' || r.data.status === 'failed') {
            clearInterval(poll); setGenerating(false);
            if (r.data.designs?.[0]) {
              const d = r.data.designs[0]; setDesign(d);
              const cols = { primary: d.primary_color||'', bg: d.bg_color||'', text: d.text_color||'' };
              setDesignColors(cols); setOriginalColors(cols); setDesignImages(extractImages(d.html));
              toast.success('Design generated!');
            } else toast.error('Failed');
          }
        } catch { /* polling */ }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setGenerating(false); }, 360000);
    } catch (err) { setGenerating(false); toast.error(err.response?.data?.detail||'Failed'); }
  };

  const handleReplaceImage = async (imgIdx, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await uploadAPI.thumbnail(fd); const newUrl = res.data.thumbnail_url;
      const oldUrl = designImages[imgIdx];
      if (oldUrl && design?.html) {
        setDesign(prev => ({ ...prev, html: prev.html.split(oldUrl).join(newUrl) }));
        setDesignImages(p => { const n=[...p]; n[imgIdx]=newUrl; return n; });
        setImageReplacements(p => ({ ...p, [oldUrl]: newUrl }));
        toast.success('Replaced!');
      }
    } catch { toast.error('Failed'); } e.target.value = '';
  };

  const saveDesignEdits = async () => {
    if (!design || !projectId) return;
    const ep = { layout, image_replacements: imageReplacements, font_sizes: { title: titleSize, subtitle: subtitleSize, body: bodySize, caption: captionSize } };
    if (designColors.primary) ep.new_primary = designColors.primary;
    if (designColors.bg) ep.new_bg = designColors.bg;
    if (designColors.text) ep.new_text = designColors.text;
    if (originalColors.primary && designColors.primary !== originalColors.primary) ep.old_primary = originalColors.primary;
    if (originalColors.bg && designColors.bg !== originalColors.bg) ep.old_bg = originalColors.bg;
    if (originalColors.text && designColors.text !== originalColors.text) ep.old_text = originalColors.text;
    try { await studioAPI.saveDesignEdits(projectId, ep); setOriginalColors({...designColors}); setImageReplacements({}); } catch { /* continue */ }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try { await saveDesignEdits(); const r = await studioAPI.exportPdf(projectId, { design_index: 0 }); const url = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = `${(uploadResult?.filename||'ebook').split('.')[0]}.pdf`; a.click(); window.URL.revokeObjectURL(url); toast.success('PDF downloaded!'); }
    catch { toast.error('Export failed'); } finally { setExporting(false); }
  };

  // Preview builder — scrollable, matches PDF output exactly
  const buildFullHtml = useCallback(() => {
    if (!design?.html) return '';
    const pageSize = layout === 'landscape' ? '9in 6in' : '6in 9in';
    const margins = '0.8in 0.65in 0.7in';
    const pc = designColors.primary || design.primary_color || '#195259';
    const bc = designColors.bg || design.bg_color || '#ffffff';
    const tc = designColors.text || design.text_color || '#333333';
    const usesCssVars = design.html.includes('var(--primary)') || design.html.includes('var(--bg)');
    const legacyOverrides = !usesCssVars ? `body,.cover,.toc,.chapter,.page,[class*="page"],section{background-color:${bc}!important}h1,h2,h3,h4,h5,h6,.chapter-title{color:${pc}!important}p,li,span,td,th{color:${tc}!important}a,strong,b{color:${pc}!important}` : '';
    const sizeOverrides = `
      :root{--title-size:${titleSize}px!important;--subtitle-size:${subtitleSize}px!important;--body-size:${bodySize}px!important;--caption-size:${captionSize}px!important;}
    `;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=DM+Serif+Display&family=Lora:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=Source+Sans+Pro:wght@300;400;600;700&family=Lato:wght@300;400;700&display=swap"><style>@page{size:${pageSize};margin:${margins}}:root{--primary:${pc}!important;--bg:${bc}!important;--text:${tc}!important;--accent:${pc}!important;}body{margin:0;padding:0;}*{box-sizing:border-box;}${legacyOverrides}${sizeOverrides}</style></head><body>${design.html}</body></html>`;
  }, [design, designColors, layout, titleSize, subtitleSize, bodySize, captionSize]);

  const requiredDone = ['topic','target_audience','main_problem','transformation'].filter(k => interviewAnswers[k]?.trim()).length;
  const totalDone = INTERVIEW_QUESTIONS.filter(q => interviewAnswers[q.key]?.trim?.()?.length > 0).length;
  const totalWords = chapters.reduce((s, c) => s + (c.content?.split(/\s+/).filter(Boolean).length||0), 0);

  const STEPS = [{ n:1, l:'Interview' },{ n:2, l:'Draft' },{ n:3, l:'Design' },{ n:4, l:'Export' }];

  return (
    <div className="space-y-5 max-w-[1400px]" data-testid="digital-studio-page">
      <style>{`
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes pglow{0%,100%{box-shadow:0 0 0 0 rgba(25,82,89,.5)}50%{box-shadow:0 0 20px 6px rgba(25,82,89,.25)}}
        .gen-btn{animation:pglow 1.5s ease-in-out infinite;background:linear-gradient(135deg,#195259,#0F766E)}.gen-btn:hover{transform:scale(1.02)}
        .gen-loading{background:linear-gradient(90deg,#195259 0%,#0F766E 50%,#195259 100%);background-size:200% 100%;animation:shimmer 2s linear infinite}
        input[type=range]{-webkit-appearance:none;height:4px;background:#e5e7eb;border-radius:2px;outline:none}
        input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;background:#195259;border-radius:50%;cursor:pointer}
      `}</style>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Sparkles className="h-6 w-6" style={{color:'#FF9A00'}} /> Digital Studio</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Create ebooks and courses</p>
        </div>
        {step > 0 && <Button variant="outline" size="sm" onClick={() => { setStep(0); Promise.all([studioAPI.projects().then(r=>setProjects(r.data)).catch(()=>{}), courseAPI.list().then(r=>setCourses(r.data)).catch(()=>{})]); }} className="gap-1.5" data-testid="my-projects-btn"><FolderOpen className="h-3.5 w-3.5" /> Digital Studio</Button>}
      </div>

      {step > 0 && (
        <div className="flex items-center gap-2" data-testid="studio-stepper">
          {STEPS.map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <button onClick={() => { if (s.n < step) setStep(s.n); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${step >= s.n ? 'text-white cursor-pointer' : 'bg-muted text-muted-foreground'}`} style={step >= s.n ? { backgroundColor: step === s.n ? '#195259' : '#10b981' } : {}}>
                {step > s.n ? <Check className="h-3 w-3" /> : <span>{s.n}</span>}<span className="hidden sm:inline">{s.l}</span>
              </button>
              {i < 3 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            </div>
          ))}
        </div>
      )}

      {/* STEP 0: Digital Studio Homepage — Clean Picker */}
      {step === 0 && (
        <div className="space-y-6" data-testid="studio-home">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ebook Creator Card */}
            <button onClick={() => setStep(-1)} className="p-8 rounded-2xl border-2 border-border hover:border-[#FF9A00] text-left transition-all hover:shadow-lg group" data-testid="pick-ebook">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#FF9A00' }}>
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: '#195259' }}>AI Ebook Creator <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{backgroundColor:'#FF9A00',color:'white'}}>Beta</span></h3>
              <p className="text-sm text-muted-foreground mt-1">Create beautiful ebooks with AI. Write, design, and export as PDF.</p>
              {projects.length > 0 && <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1"><BookOpen className="h-3 w-3" /> {projects.length} ebook{projects.length !== 1 ? 's' : ''}</p>}
            </button>
            {/* Course Builder Card */}
            <button onClick={() => setStep(-2)} className="p-8 rounded-2xl border-2 border-border hover:border-[#195259] text-left transition-all hover:shadow-lg group" data-testid="pick-course">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#195259' }}>
                <Play className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: '#195259' }}>Course Builder</h3>
              <p className="text-sm text-muted-foreground mt-1">Build video courses with modules and lessons. Publish to Product Library.</p>
              {courses.length > 0 && <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1"><Play className="h-3 w-3" /> {courses.length} course{courses.length !== 1 ? 's' : ''}</p>}
            </button>
          </div>
        </div>
      )}

      {/* EBOOK HUB (step -1) */}
      {step === -1 && (
        <div className="space-y-4" data-testid="ebook-hub">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setStep(0)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-5 w-5" /></button>
            <h2 className="text-lg font-semibold">AI Ebook Creator</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => startNew('ai')} className="p-4 rounded-xl border-2 border-dashed border-[#FF9A00]/40 hover:border-[#FF9A00] text-left transition-all hover:shadow-md" data-testid="new-ai-ebook"><Plus className="h-5 w-5 text-[#FF9A00] mb-1.5" /><p className="text-sm font-semibold">Create with AI</p><p className="text-xs text-muted-foreground mt-0.5">AI writes your ebook</p></button>
            <button onClick={() => startNew('upload')} className="p-4 rounded-xl border-2 border-dashed border-border hover:border-[#195259] text-left transition-all hover:shadow-md" data-testid="new-upload-ebook"><Upload className="h-5 w-5 text-muted-foreground mb-1.5" /><p className="text-sm font-semibold">Upload Document</p><p className="text-xs text-muted-foreground mt-0.5">Upload DOCX, PDF</p></button>
          </div>
          {projects.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-muted-foreground">My Ebooks</p>
              {projects.map(p => (
                <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-xl border hover:border-[#195259]/40 transition-all" data-testid={`project-${p.id}`}>
                  <button onClick={() => loadProject(p.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{backgroundColor:'#195259'}}><BookOpen className="h-3.5 w-3.5 text-white" /></div>
                    <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{p.filename||'Untitled'}</p><p className="text-[10px] text-muted-foreground">{p.gen_status==='done'?'Design ready':p.draft_status==='confirmed'?'Draft ready':'In progress'}</p></div>
                  </button>
                  <span className="text-[10px] text-muted-foreground shrink-0">{new Date(p.created_at).toLocaleDateString()}</span>
                  <button onClick={() => loadProject(p.id)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground" title="Edit"><PenSquare className="h-3.5 w-3.5" /></button>
                  <button onClick={async () => {
                    if (!window.confirm('Delete this ebook project?')) return;
                    try { await studioAPI.deleteProject(p.id); setProjects(prev => prev.filter(x => x.id !== p.id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
                  }} className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COURSE HUB (step -2) */}
      {step === -2 && (
        <div className="space-y-4" data-testid="course-hub">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setStep(0)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-5 w-5" /></button>
            <h2 className="text-lg font-semibold">Course Builder</h2>
          </div>
          <button onClick={() => navigate('/app/studio/course/new')} className="w-full p-4 rounded-xl border-2 border-dashed border-[#195259]/40 hover:border-[#195259] text-left transition-all hover:shadow-md" data-testid="new-course">
            <Plus className="h-5 w-5 text-[#195259] mb-1.5" />
            <p className="text-sm font-semibold">Create New Course</p>
            <p className="text-xs text-muted-foreground mt-0.5">Build modules, add video lessons, publish to Product Library</p>
          </button>
          {courses.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-muted-foreground">My Courses</p>
              {courses.map(c => (
                <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-xl border hover:border-[#195259]/40 transition-all" data-testid={`course-${c.id}`}>
                  <button onClick={() => navigate(`/app/studio/course/${c.id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{backgroundColor:'#195259'}}><Play className="h-3.5 w-3.5 text-white" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{c.title}</p>
                      <p className="text-[10px] text-muted-foreground">{c.module_count} modules &middot; {c.lesson_count} lessons</p>
                    </div>
                  </button>
                  {c.is_published ? (
                    <button onClick={async () => { try { await courseAPI.unpublish(c.id); setCourses(prev => prev.map(x => x.id === c.id ? {...x, is_published: false} : x)); toast.success('Unpublished'); } catch { toast.error('Failed'); }}}
                      className="text-[9px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium hover:bg-red-100 hover:text-red-700" title="Click to unpublish">Live</button>
                  ) : (
                    <button onClick={async () => { try { await courseAPI.publish(c.id); setCourses(prev => prev.map(x => x.id === c.id ? {...x, is_published: true} : x)); toast.success('Published!'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }}}
                      className="text-[9px] px-2 py-1 rounded-full bg-gray-100 text-gray-500 font-medium hover:bg-emerald-100 hover:text-emerald-700" title="Click to publish">Draft</button>
                  )}
                  <button onClick={() => navigate(`/app/studio/course/${c.id}`)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground" title="Edit"><PenSquare className="h-3.5 w-3.5" /></button>
                  <button onClick={async () => {
                    if (!window.confirm(`Delete "${c.title}"?`)) return;
                    try { await courseAPI.delete(c.id); setCourses(prev => prev.filter(x => x.id !== c.id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
                  }} className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 1: Interview / Upload */}
      {step === 1 && (
        <div className="space-y-3" data-testid="studio-step-1">
          {inputMode === 'upload' ? (
            <Card><CardHeader><CardTitle>Upload Document</CardTitle></CardHeader><CardContent>
              <label className="block cursor-pointer"><input ref={fileInputRef} type="file" accept=".docx,.pdf,.xlsx,.xls,.csv" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-input" />
                <div className="border-2 border-dashed rounded-xl p-10 text-center hover:border-[#195259]/50">{uploading ? <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{color:'#195259'}} /> : <><Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" /><p className="text-sm">Click to upload</p></>}</div>
              </label>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              <div className="rounded-xl p-3 flex items-center justify-between" style={{background:'linear-gradient(135deg,#195259,#0F766E)'}}>
                <div><h2 className="text-white font-semibold text-sm">AI Interview</h2><p className="text-white/60 text-[10px]">Q1-Q4 required</p></div>
                <span className="text-white font-bold text-sm">{totalDone}/10</span>
              </div>
              {INTERVIEW_QUESTIONS.map((q, idx) => {
                const val = interviewAnswers[q.key]; const done = val?.trim?.()?.length > 0; const active = currentQ === idx;
                return (
                  <div key={q.key} onClick={() => setCurrentQ(idx)} className={`rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${active?'border-[#195259] bg-[#f0fdfa]':done?'border-emerald-300':'border-border'}`} data-testid={`interview-q-${idx}`}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${done?'bg-emerald-100 text-emerald-600':active?'bg-[#195259] text-white':'bg-muted text-muted-foreground'}`}>{done?<Check className="h-2.5 w-2.5" />:idx+1}</div>
                      <p className="text-xs font-medium flex-1">{q.label}{q.required && <span className="text-destructive ml-0.5">*</span>}</p>
                      {done && !active && <span className="text-[10px] text-muted-foreground truncate max-w-[150px]">{val}</span>}
                    </div>
                    {active && <div className="px-3 pb-2 border-t">
                      {q.type==='text' && <Input value={val||''} onChange={e=>setInterviewAnswers(p=>({...p,[q.key]:e.target.value}))} placeholder={q.placeholder} className="mt-1.5 text-xs" autoFocus data-testid={`input-${q.key}`} />}
                      {q.type==='textarea' && <Textarea value={val||''} onChange={e=>setInterviewAnswers(p=>({...p,[q.key]:e.target.value}))} placeholder={q.placeholder} rows={2} className="mt-1.5 text-xs" autoFocus data-testid={`input-${q.key}`} />}
                      {q.type==='select' && <div className="flex flex-wrap gap-1.5 mt-1.5">{q.options.map(o=>(<button key={o.value} onClick={e=>{e.stopPropagation();setInterviewAnswers(p=>({...p,[q.key]:o.value}))}} className={`px-2.5 py-1 rounded border text-[10px] font-medium ${val===o.value?'border-[#195259] bg-[#195259]/10':'border-border'}`}>{o.label}</button>))}</div>}
                      {idx<9 && <button onClick={e=>{e.stopPropagation();setCurrentQ(idx+1)}} className="mt-1 text-[10px] font-medium" style={{color:'#195259'}}>Next &rsaquo;</button>}
                    </div>}
                  </div>
                );
              })}
              <Button onClick={handleGenerateDraft} disabled={savingInterview||requiredDone<4} className="w-full gen-btn text-white py-4 text-sm" data-testid="generate-draft-btn">
                {savingInterview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {requiredDone<4 ? `Answer required (${requiredDone}/4)` : `Generate Draft`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Draft */}
      {step === 2 && (
        <div className="space-y-3" data-testid="studio-step-2">
          {draftGenerating ? (
            <Card><CardContent className="py-12 text-center"><Sparkles className="h-8 w-8 animate-pulse mx-auto mb-3" style={{color:'#FF9A00'}} /><p className="font-medium text-sm">Writing draft...</p><div className="w-40 h-1 rounded-full overflow-hidden bg-muted mx-auto mt-3"><div className="h-full gen-loading w-full" /></div></CardContent></Card>
          ) : (<>
            <div className="flex items-center justify-between">
              <div><h2 className="text-base font-semibold flex items-center gap-2"><PenLine className="h-4 w-4" style={{color:'#195259'}} /> Draft</h2><p className="text-xs text-muted-foreground">{chapters.length} chapters &middot; {totalWords.toLocaleString()} words</p></div>
              <div className="flex gap-1.5">
                {inputMode==='ai' && <Button variant="outline" size="sm" onClick={async()=>{setDraftGenerating(true);setChapters([]);try{await studioAPI.generateDraft(projectId);pollDraft(projectId)}catch{setDraftGenerating(false)}}} className="gap-1 text-xs h-7"><RotateCcw className="h-3 w-3" />Regen All</Button>}
                <Button variant="outline" size="sm" onClick={()=>copyText(chaptersToText(chapters),'All')} className="gap-1 text-xs h-7"><Copy className="h-3 w-3" />Copy</Button>
              </div>
            </div>
            <div className="space-y-1.5">
              {chapters.map((ch, idx) => {
                const exp=expandedIdx===idx; const editing=editingIdx===idx; const regen=regeneratingChapter===idx;
                return (
                  <div key={idx} className={`rounded-lg border-2 overflow-hidden transition-all ${exp?'border-[#195259]/30':'border-border'}`} data-testid={`chapter-${idx}`}>
                    <button onClick={()=>setExpandedIdx(exp?-1:idx)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent/20">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{backgroundColor:idx===0?'#FF9A00':'#195259'}}>{idx===0?<BookOpen className="h-2.5 w-2.5" />:idx}</div>
                      <div className="flex-1 min-w-0"><p className="text-[9px] uppercase tracking-wider text-muted-foreground">{idx===0?'Ebook Title':`Chapter ${idx}`}</p><p className="text-xs font-semibold truncate">{ch.title}</p></div>
                      <span className="text-[9px] text-muted-foreground mr-1">{ch.content.split(/\s+/).filter(Boolean).length}w</span>
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${exp?'rotate-180':''}`} />
                    </button>
                    {exp && <div className="px-3 pb-3 border-t">
                      <div className="flex gap-1.5 py-1.5">
                        <button onClick={()=>setEditingIdx(editing?null:idx)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${editing?'bg-[#195259] text-white':'bg-muted text-muted-foreground'}`}>{editing?'Done':'Edit'}</button>
                        <button onClick={()=>copyText(`## ${ch.title}\n\n${ch.content}`,ch.title)} className="px-2 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">Copy</button>
                        {inputMode==='ai' && <button onClick={()=>handleRegenerateChapter(idx)} disabled={regen} className="px-2 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">{regen?'Regenerating...':'Regen'}</button>}
                      </div>
                      {regen ? <div className="py-4 text-center text-xs text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />Rewriting...</div>
                      : editing ? <div className="space-y-1.5"><Input value={ch.title} onChange={e=>{const n=[...chapters];n[idx]={...n[idx],title:e.target.value};setChapters(n)}} className="font-semibold text-xs" /><Textarea value={ch.content} onChange={e=>{const n=[...chapters];n[idx]={...n[idx],content:e.target.value};setChapters(n)}} className="font-mono text-xs min-h-[200px]" /></div>
                      : <div className="text-xs leading-relaxed whitespace-pre-wrap text-foreground/80 pt-1 max-h-[300px] overflow-y-auto">{ch.content}</div>}
                    </div>}
                  </div>
                );
              })}
            </div>
            <Button onClick={handleConfirmDraft} disabled={savingDraft||!chapters.length} className="w-full gen-btn text-white py-4 gap-2 text-sm" data-testid="confirm-draft-btn">
              {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm Draft
            </Button>
          </>)}
        </div>
      )}

      {/* STEP 3: Design (Config → Editor with page-by-page preview) */}
      {step === 3 && (
        <div className="space-y-4" data-testid="studio-step-3">
          {!design && !generating ? (
            <Card>
              <CardHeader><CardTitle>Design Your Ebook</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1.5"><Palette className="h-3.5 w-3.5" /> Color Mood</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                    {COLOR_MOODS.map(m => (
                      <button key={m.value} onClick={()=>setColorMood(m.value)} className={`p-2 rounded-lg border-2 text-center transition-all ${colorMood===m.value?'border-[#195259] shadow':'border-border'}`} data-testid={`mood-${m.value}`}>
                        <div className="flex justify-center gap-0.5 mb-1">{m.colors.map((c,i)=><div key={i} className="w-3.5 h-3.5 rounded-full" style={{backgroundColor:c}} />)}</div>
                        <p className="text-[9px] font-medium">{m.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  {[['portrait','Portrait',Smartphone],['landscape','Landscape',Monitor]].map(([v,l,I])=>(
                    <button key={v} onClick={()=>setLayout(v)} className={`flex-1 p-3 rounded-lg border-2 text-center ${layout===v?'border-[#195259] bg-accent':'border-border'}`}>
                      <I className="h-5 w-5 mx-auto mb-1 text-muted-foreground" /><p className="text-xs font-medium">{l}</p>
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Label className="text-xs shrink-0">Logo:</Label>
                  <button onClick={()=>{setLogoChoice('none');setBrandLogos([])}} className={`px-2.5 py-1 rounded border text-[10px] ${logoChoice==='none'?'border-[#195259] bg-accent':'border-border'}`}>None</button>
                  {brandLogos.map((u,i)=><div key={i} className="w-10 h-10 rounded border overflow-hidden"><img src={u} alt="" className="w-full h-full object-contain" /></div>)}
                  {brandLogos.length<3 && <label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} /><div className="w-10 h-10 rounded border-2 border-dashed flex items-center justify-center">{uploadingLogo?<Loader2 className="h-3 w-3 animate-spin" />:<ImagePlus className="h-3 w-3 text-muted-foreground" />}</div></label>}
                </div>
                <Button onClick={handleGenerate} className="w-full gen-btn text-white py-4 gap-2 text-sm" data-testid="generate-design-btn"><Sparkles className="h-4 w-4" /> Generate Design</Button>
              </CardContent>
            </Card>
          ) : generating ? (
            <Card><CardContent className="py-12 text-center"><Sparkles className="h-8 w-8 animate-pulse mx-auto mb-3" style={{color:'#FF9A00'}} /><p className="font-medium text-sm">Designing ebook...</p><p className="text-xs text-muted-foreground mt-1">~30-60s</p><div className="w-40 h-1 rounded-full overflow-hidden bg-muted mx-auto mt-3"><div className="h-full gen-loading w-full" /></div></CardContent></Card>
          ) : design ? (
            /* Split layout: Left controls + Right page preview */
            <div className="flex gap-4" style={{ minHeight: '70vh' }}>
              {/* Left: Controls */}
              <div className="w-64 shrink-0 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Design Editor</h3>
                  <button onClick={()=>{setDesign(null);setDesignColors({primary:'',bg:'',text:''});setOriginalColors({primary:'',bg:'',text:''})}} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="redesign-btn"><RotateCcw className="h-3 w-3" />Redesign</button>
                </div>

                {/* Colors */}
                <div className="space-y-2 p-3 rounded-lg border bg-accent/20">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Colors</p>
                  {[['primary','Primary'],['bg','Background'],['text','Text']].map(([k,l])=>(
                    <label key={k} className="flex items-center gap-2">
                      <input type="color" value={designColors[k]||'#000'} onChange={e=>setDesignColors(p=>({...p,[k]:e.target.value}))} className="w-6 h-6 rounded border cursor-pointer" style={{padding:0}} data-testid={`color-${k}`} />
                      <span className="text-xs">{l}</span>
                    </label>
                  ))}
                </div>

                {/* Typography — 4 granular sliders */}
                <div className="space-y-2 p-3 rounded-lg border bg-accent/20">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Typography</p>
                  <div>
                    <div className="flex justify-between"><span className="text-[10px]">Chapter Title</span><span className="text-[10px] text-muted-foreground">{titleSize}px</span></div>
                    <input type="range" min="20" max="48" value={titleSize} onChange={e=>setTitleSize(+e.target.value)} className="w-full" data-testid="title-size" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-[10px]">Sub-heading</span><span className="text-[10px] text-muted-foreground">{subtitleSize}px</span></div>
                    <input type="range" min="14" max="28" value={subtitleSize} onChange={e=>setSubtitleSize(+e.target.value)} className="w-full" data-testid="subtitle-size" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-[10px]">Body Text</span><span className="text-[10px] text-muted-foreground">{bodySize}px</span></div>
                    <input type="range" min="11" max="22" value={bodySize} onChange={e=>setBodySize(+e.target.value)} className="w-full" data-testid="body-size" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-[10px]">Caption</span><span className="text-[10px] text-muted-foreground">{captionSize}px</span></div>
                    <input type="range" min="9" max="16" value={captionSize} onChange={e=>setCaptionSize(+e.target.value)} className="w-full" data-testid="caption-size" />
                  </div>
                </div>

                {/* Images */}
                {designImages.length > 0 && (
                  <div className="space-y-2 p-3 rounded-lg border bg-accent/20">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Images</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {designImages.map((url, i) => (
                        <label key={i} className="relative aspect-square rounded overflow-hidden cursor-pointer group hover:ring-2 hover:ring-[#FF9A00]">
                          <input type="file" accept="image/*" className="hidden" onChange={(e)=>handleReplaceImage(i,e)} data-testid={`replace-img-${i}`} />
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center"><ImagePlus className="h-3 w-3 text-white" /></div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Layout — triggers redesign */}
                <div className="space-y-2 p-3 rounded-lg border bg-accent/20">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Layout</p>
                  <div className="flex gap-1.5">
                    {['portrait','landscape'].map(v=>(
                      <button key={v} onClick={()=>{
                        if (v !== layout) {
                          setLayout(v); setDesign(null); setDesignColors({primary:'',bg:'',text:''}); setOriginalColors({primary:'',bg:'',text:''});
                          toast.info(`Regenerating for ${v}...`);
                          setTimeout(async () => {
                            setGenerating(true);
                            try {
                              await studioAPI.generate(projectId, { layout: v, color_mood: colorMood, brand_logos: brandLogos, ebook_images: ebookImages });
                              const poll = setInterval(async () => { try { const r = await studioAPI.status(projectId); if (r.data.status==='done'||r.data.status==='failed') { clearInterval(poll); setGenerating(false); if (r.data.designs?.[0]) { const d=r.data.designs[0]; setDesign(d); const c={primary:d.primary_color||'',bg:d.bg_color||'',text:d.text_color||''}; setDesignColors(c); setOriginalColors(c); setDesignImages(extractImages(d.html)); toast.success('Done!'); } } } catch {/* polling */} }, 5000);
                              setTimeout(()=>{clearInterval(poll);setGenerating(false)}, 360000);
                            } catch { setGenerating(false); }
                          }, 100);
                        }
                      }} className={`flex-1 py-1.5 rounded text-[10px] font-medium border ${layout===v?'border-[#195259] bg-[#195259] text-white':'border-border'}`} data-testid={`layout-${v}`}>{v==='portrait'?'Portrait':'Landscape'}</button>
                    ))}
                  </div>
                </div>

                <Button onClick={async()=>{await saveDesignEdits();setStep(4)}} className="w-full gen-btn text-white py-3 gap-2 text-xs" data-testid="go-to-export">
                  <Download className="h-3.5 w-3.5" /> Continue to Export
                </Button>
              </div>

              {/* Right: Scrollable preview (matches PDF exactly) */}
              <div className="flex-1 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Ebook Preview</span>
                  <span className="text-xs text-muted-foreground">Scroll to view all content</span>
                </div>
                <div className="rounded-xl overflow-hidden border shadow-lg mx-auto" style={{ height: '75vh', minHeight: '500px', maxWidth: layout==='landscape'?'900px':'650px' }}>
                  <iframe srcDoc={buildFullHtml()} className="w-full h-full" title="Ebook Preview" style={{ border:'none' }} data-testid="design-preview" />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* STEP 4: Export */}
      {step === 4 && (
        <div className="space-y-5" data-testid="studio-step-4">
          <div className="text-center"><h2 className="text-xl font-semibold">Your Ebook is Ready!</h2><p className="text-sm text-muted-foreground">Scroll to preview. What you see is what you get in the PDF.</p></div>
          {design && (
            <div className="rounded-xl overflow-hidden border shadow-lg mx-auto" style={{ height: '75vh', minHeight: '500px', maxWidth: layout==='landscape'?'900px':'650px' }}>
              <iframe srcDoc={buildFullHtml()} className="w-full h-full" title="Export Preview" style={{ border:'none' }} data-testid="export-preview" />
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="lg" onClick={()=>setStep(3)} className="gap-2"><ArrowLeft className="h-4 w-4" /> Edit Design</Button>
            <Button onClick={handleExportPdf} disabled={exporting} size="lg" className="gap-2 text-white px-10 py-6 text-base font-semibold" style={{backgroundColor:'#FF9A00'}} data-testid="download-pdf-btn">
              {exporting ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />} Download as PDF
            </Button>
            <Button variant="outline" size="lg" onClick={() => { setListForm(p => ({...p, name: uploadResult?.filename || ''})); setShowListPopup(true); }} className="gap-2" data-testid="list-product-btn">
              <Package className="h-4 w-4" /> List in Product Library
            </Button>
          </div>
          <p className="text-center text-xs text-muted-foreground">Saved. Revisit anytime from My Ebooks.</p>
        </div>
      )}

      {/* List Product Popup */}
      {showListPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowListPopup(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold" style={{ color: '#195259' }}>List Ebook in Product Library</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium">Product Name</Label>
                <Input value={listForm.name} onChange={e => setListForm(p => ({...p, name: e.target.value}))} placeholder="Ebook name" data-testid="list-name" />
              </div>
              <div>
                <Label className="text-xs font-medium">Description</Label>
                <Textarea value={listForm.description} onChange={e => setListForm(p => ({...p, description: e.target.value}))} placeholder="Short description" rows={2} data-testid="list-desc" />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label className="text-xs font-medium">Pricing</Label>
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => setListForm(p => ({...p, is_free: false}))} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${!listForm.is_free ? 'border-[#195259] bg-[#195259] text-white' : 'border-border'}`}>Paid</button>
                    <button onClick={() => setListForm(p => ({...p, is_free: true}))} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${listForm.is_free ? 'border-[#195259] bg-[#195259] text-white' : 'border-border'}`}>Free</button>
                  </div>
                </div>
                {!listForm.is_free && (
                  <div className="w-28">
                    <Label className="text-xs font-medium">Price (RM)</Label>
                    <Input type="number" min="0" step="0.01" value={listForm.price} onChange={e => setListForm(p => ({...p, price: parseFloat(e.target.value) || 0}))} data-testid="list-price" />
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Label className="text-xs font-medium">Max Downloads</Label>
                  <Input type="number" min="1" value={listForm.max_downloads} onChange={e => setListForm(p => ({...p, max_downloads: parseInt(e.target.value) || 5}))} data-testid="list-max-dl" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs font-medium">Validity (days)</Label>
                  <Input type="number" min="1" value={listForm.validity_days} onChange={e => setListForm(p => ({...p, validity_days: parseInt(e.target.value) || 365}))} data-testid="list-validity" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowListPopup(false)} className="flex-1">Cancel</Button>
              <Button onClick={async () => {
                try {
                  await studioAPI.listProduct(projectId, listForm);
                  toast.success('Ebook listed in Product Library!');
                  setShowListPopup(false);
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }} className="flex-1 text-white" style={{backgroundColor:'#195259'}} data-testid="list-confirm-btn">List Product</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
